import React from 'react';
import { Link } from 'react-router-dom';
import DoshaWheel from './DoshaWheel';
import { recommendations, serviceRecommendations } from '../../data/recommendations';
import services from '../../data/services';

const ResultsDisplay = ({ results, onStartOver }) => {
  const { scores, primaryDosha, secondaryDosha, constitution, constitutionType, balanceDescription } = results;
  
  const primaryDoshaData = recommendations[primaryDosha];
  const secondaryDoshaData = secondaryDosha ? recommendations[secondaryDosha] : null;


  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="min-h-screen bg-warm-white py-8">
      <div className="max-w-6xl mx-auto px-4">
        {/* Hero Section */}
        <div className="text-center mb-12 animate-fade-in-up">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-secondary rounded-full mb-6">
            <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
          </div>
          <h1 className="font-display text-4xl md:text-5xl font-bold text-primary-dark mb-4">
            Your Dosha Results
          </h1>
          <p className="text-xl text-gray-600 mx-auto">
            Discover your unique Ayurvedic constitution and personalized recommendations for optimal health and balance.
          </p>
        </div>

        {/* Results Overview */}
        <div className="grid lg:grid-cols-2 gap-8 mb-12">
          {/* Dosha Wheel */}
          <div className="card-wellness p-8 text-center animate-fade-in-up">
            <h2 className="font-display text-2xl font-bold text-primary-dark mb-6">
              Your Constitution
            </h2>
            <DoshaWheel scores={scores} size={240} />
            <div className="mt-6 p-4 bg-blue-50 rounded-lg">
              <h3 className="font-semibold text-primary-dark mb-2">
                Primary Constitution:
                <div>{constitution.toUpperCase()}</div>
              </h3>
              <p className="text-gray-600 text-sm leading-relaxed">
                {balanceDescription}
              </p>
            </div>
          </div>

          {/* Primary Dosha Details */}
          <div className="card-wellness p-8 animate-fade-in-up">
            <div className={`p-6 rounded-xl mb-6 dosha-${primaryDosha}`}>
              <h2 className="font-display text-2xl font-bold text-gray-800 mb-2">
                {primaryDoshaData.name} Dominant
              </h2>
              <div className="text-gray-700 font-medium mb-3">
                {primaryDoshaData.element} • {primaryDoshaData.qualities.join(' • ')}
              </div>
              <p className="text-gray-700 leading-relaxed">
                {primaryDoshaData.description}
              </p>
            </div>

            {/* Balanced vs Imbalanced Traits */}
            <div className="grid md:grid-cols-2 gap-4">
              <div className="bg-green-50 p-4 rounded-lg">
                <h4 className="font-semibold text-green-800 mb-3 flex items-center">
                  <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  When Balanced
                </h4>
                <ul className="space-y-1">
                  {primaryDoshaData.balancedTraits.map((trait, index) => (
                    <li key={index} className="text-sm text-green-700">• {trait}</li>
                  ))}
                </ul>
              </div>

              <div className="bg-red-50 p-4 rounded-lg">
                <h4 className="font-semibold text-red-800 mb-3 flex items-center">
                  <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                  When Imbalanced
                </h4>
                <ul className="space-y-1">
                  {primaryDoshaData.imbalancedTraits.map((trait, index) => (
                    <li key={index} className="text-sm text-red-700">• {trait}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Recommendations Sections */}
        <div className="space-y-8">
          {/* Recommended Treatments */}
          <RecommendedTreatments
            primaryDosha={primaryDosha}
          />

          {/* Diet Guidelines */}
          <DietGuidelines dietGuidelines={primaryDoshaData.dietGuidelines} />

          {/* Lifestyle Recommendations */}
          <LifestyleRecommendations 
            lifestyle={primaryDoshaData.lifestyle}
            seasonalAdvice={primaryDoshaData.seasonalAdvice}
          />
        </div>

        {/* Action Buttons */}
        <div className="mt-12 text-center space-y-4">
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={handlePrint}
              className="btn-primary text-lg px-8 py-3 rounded-full font-display flex items-center justify-center gap-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
              </svg>
              Print Results
            </button>
            <button
              onClick={onStartOver}
              className="btn-secondary text-lg px-8 py-3 rounded-full font-display"
            >
              Take Quiz Again
            </button>
          </div>
          
          <div className="pt-6">
            <Link
              to="/services"
              className="btn-gold text-lg px-8 py-4 rounded-full font-display inline-block"
            >
              Book Recommended Treatments
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

// Sub-components
const RecommendedTreatments = ({ primaryDosha }) => {
  const recommendedServices = serviceRecommendations[primaryDosha]?.primary || [];
  
  return (
    <div className="card-wellness p-8 animate-fade-in-up">
      <h3 className="font-display text-2xl font-bold text-primary-dark mb-6">
        Recommended Treatments
      </h3>
      <div className="grid md:grid-cols-3 gap-4">
        {recommendedServices.slice(0, 3).map((serviceKey) => {
          const service = services[serviceKey];
          if (!service) return null;
          
          return (
            <div key={serviceKey} className="bg-blue-50 p-4 rounded-lg text-center hover:bg-blue-100 transition-colors">
              <div className="font-semibold text-primary-dark mb-2">
                {service.title}
              </div>
              <p className="text-xs text-gray-600 mb-3 line-clamp-2">
                {service.brief}
              </p>
              <Link
                to={`/services/${serviceKey}`}
                className="text-sm text-primary hover:text-primary-dark transition-colors font-medium"
              >
                Learn More →
              </Link>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const DietGuidelines = ({ dietGuidelines }) => (
  <div className="card-wellness p-8 animate-fade-in-up">
    <h3 className="font-display text-2xl font-bold text-primary-dark mb-6">
      Dietary Guidelines
    </h3>
    <div className="grid md:grid-cols-2 gap-6">
      <div className="bg-green-50 p-6 rounded-lg">
        <h4 className="font-semibold text-green-800 mb-4 flex items-center">
          <span className="text-green-600 mr-2">✓</span>
          Foods to Favor
        </h4>
        <ul className="space-y-2">
          {dietGuidelines.favor.map((item, index) => (
            <li key={index} className="text-green-700 text-sm">• {item}</li>
          ))}
        </ul>
      </div>
      
      <div className="bg-red-50 p-6 rounded-lg">
        <h4 className="font-semibold text-red-800 mb-4 flex items-center">
          <span className="text-red-600 mr-2">✕</span>
          Foods to Minimize
        </h4>
        <ul className="space-y-2">
          {dietGuidelines.avoid.map((item, index) => (
            <li key={index} className="text-red-700 text-sm">• {item}</li>
          ))}
        </ul>
      </div>
    </div>
  </div>
);

const LifestyleRecommendations = ({ lifestyle, seasonalAdvice }) => (
  <div className="card-wellness p-8 animate-fade-in-up">
    <h3 className="font-display text-2xl font-bold text-primary-dark mb-6">
      Lifestyle Recommendations
    </h3>
    <div className="grid md:grid-cols-2 gap-6">
      <div>
        <h4 className="font-semibold text-primary-dark mb-4">Daily Practices</h4>
        <ul className="space-y-2">
          {lifestyle.map((item, index) => (
            <li key={index} className="text-gray-700 text-sm flex items-start">
              <span className="text-secondary mr-2 mt-1">•</span>
              {item}
            </li>
          ))}
        </ul>
      </div>
      
      <div>
        <h4 className="font-semibold text-primary-dark mb-4">Seasonal Guidance</h4>
        <div className="space-y-3">
          {Object.entries(seasonalAdvice).map(([season, advice]) => (
            <div key={season} className="text-sm">
              <span className="font-medium text-primary capitalize">{season}:</span>
              <p className="text-gray-600 mt-1">{advice}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  </div>
);

// Helper function to generate email body
const generateEmailBody = (results) => {
  const { scores, constitution, balanceDescription } = results;
  return `My Ayurvedic Dosha Assessment Results

Constitution: ${constitution.toUpperCase()}
Scores: Vata ${scores.vata}%, Pitta ${scores.pitta}%, Kapha ${scores.kapha}%

${balanceDescription}

For detailed recommendations and to book treatments, visit: ${window.location.origin}/services

Completed on: ${new Date().toLocaleDateString()}
`;
};

export default ResultsDisplay;
